import { useState, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Link } from 'react-router-dom'

export interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  marketing: boolean
}

export const COOKIE_CONSENT_KEY = 'gdb_cookie_consent'
export const COOKIE_PREFERENCES_KEY = 'gdb_cookie_preferences'

/**
 * Hook to check and manage cookie consent preferences.
 *
 * Usage:
 * ```tsx
 * const { hasConsent, preferences, isConsentGiven } = useCookieConsent()
 *
 * if (hasConsent('analytics')) {
 *   // Load Google Analytics, etc.
 * }
 * ```
 */
export function useCookieConsent() {
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })
  const [isConsentGiven, setIsConsentGiven] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY)

    if (consent) {
      setIsConsentGiven(true)
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs))
        } catch {
          // Invalid JSON — use defaults
        }
      }
    }

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === COOKIE_CONSENT_KEY) {
        setIsConsentGiven(e.newValue === 'true')
      }
      if (e.key === COOKIE_PREFERENCES_KEY && e.newValue) {
        try {
          setPreferences(JSON.parse(e.newValue))
        } catch {
          // Invalid JSON
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const hasConsent = useCallback(
    (type: keyof CookiePreferences): boolean => {
      if (!mounted) return false
      if (type === 'necessary') return true
      return isConsentGiven && preferences[type]
    },
    [mounted, isConsentGiven, preferences],
  )

  const updatePreferences = useCallback(
    (newPrefs: Partial<CookiePreferences>) => {
      const updated = { ...preferences, ...newPrefs }
      setPreferences(updated)
      localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
      localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(updated))
      setIsConsentGiven(true)
    },
    [preferences],
  )

  const revokeConsent = useCallback(() => {
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    localStorage.removeItem(COOKIE_PREFERENCES_KEY)
    setIsConsentGiven(false)
    setPreferences({ necessary: true, analytics: false, marketing: false })
  }, [])

  return { preferences, isConsentGiven, hasConsent, updatePreferences, revokeConsent, mounted }
}

export default function CookieConsent() {
  const [mounted, setMounted] = useState(false)
  const [showBanner, setShowBanner] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  })

  useEffect(() => {
    setMounted(true)
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY)
    if (!consent) {
      const timer = setTimeout(() => setShowBanner(true), 1000)
      return () => clearTimeout(timer)
    } else {
      const savedPrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY)
      if (savedPrefs) {
        try {
          setPreferences(JSON.parse(savedPrefs))
        } catch {
          // Invalid JSON — use defaults
        }
      }
    }
    // Explicit undefined: the consent branch above returns a cleanup, so every
    // path must return something or TS7030 fires on the implicit-return branch.
    return undefined
  }, [])

  const saveConsent = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(prefs))
    setPreferences(prefs)
    setShowBanner(false)
    setShowPreferences(false)
  }

  const acceptAll = () =>
    saveConsent({ necessary: true, analytics: true, marketing: true })

  const acceptNecessaryOnly = () =>
    saveConsent({ necessary: true, analytics: false, marketing: false })

  const saveCurrentPreferences = () => saveConsent(preferences)

  if (!mounted || !showBanner) return null

  return (
    <AnimatePresence>
      {showBanner && (
        <>
          {/* Backdrop for preferences modal */}
          {showPreferences && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 z-[9998]"
              onClick={() => setShowPreferences(false)}
            />
          )}

          {/* Cookie banner — wrapped in marketing-chrome so scoped styles apply */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="marketing-chrome fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          >
            <div className="max-w-4xl mx-auto bg-dark-2 border border-[var(--color-dark-4)] rounded shadow-2xl overflow-hidden">
              {!showPreferences ? (
                /* Main banner */
                <div className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <h4 className="text-white mb-2">Cookie Settings</h4>
                      <p className="text-muted-godlike text-sm">
                        We use cookies to enhance your experience on our website. Some cookies
                        are essential for the website to function, while others help us improve
                        the site and provide personalised content.{' '}
                        <Link to="/privacy" className="text-accent-godlike hover:underline">
                          Privacy Policy
                        </Link>
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <button
                        onClick={() => setShowPreferences(true)}
                        className="btn-godlike btn-godlike-secondary btn-godlike-sm"
                      >
                        Preferences
                      </button>
                      <button
                        onClick={acceptNecessaryOnly}
                        className="btn-godlike btn-godlike-outline btn-godlike-sm"
                      >
                        Necessary Only
                      </button>
                      <button
                        onClick={acceptAll}
                        className="btn-godlike btn-godlike-primary btn-godlike-sm"
                      >
                        Accept All
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Preferences panel */
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-white">Manage Cookie Preferences</h4>
                    <button
                      onClick={() => setShowPreferences(false)}
                      className="text-muted-godlike hover:text-white"
                      aria-label="Close preferences"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="space-y-4 mb-6">
                    {/* Necessary */}
                    <div className="flex items-start justify-between p-4 bg-dark-3 rounded">
                      <div className="flex-1 pr-4">
                        <h5 className="text-white mb-1">Necessary Cookies</h5>
                        <p className="text-muted-godlike text-sm">
                          These cookies are essential for the website to function properly and
                          cannot be disabled.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked
                          disabled
                          className="w-5 h-5 rounded cursor-not-allowed opacity-60"
                        />
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="flex items-start justify-between p-4 bg-dark-3 rounded">
                      <div className="flex-1 pr-4">
                        <h5 className="text-white mb-1">Analytics Cookies</h5>
                        <p className="text-muted-godlike text-sm">
                          These cookies help us understand how visitors interact with our website
                          so we can improve it.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() =>
                            setPreferences({ ...preferences, analytics: !preferences.analytics })
                          }
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences.analytics
                              ? 'bg-[var(--color-main-1)]'
                              : 'bg-[var(--color-dark-4)]'
                          }`}
                          aria-pressed={preferences.analytics}
                          aria-label="Toggle analytics cookies"
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                              preferences.analytics ? 'translate-x-7' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    {/* Marketing */}
                    <div className="flex items-start justify-between p-4 bg-dark-3 rounded">
                      <div className="flex-1 pr-4">
                        <h5 className="text-white mb-1">Marketing Cookies</h5>
                        <p className="text-muted-godlike text-sm">
                          These cookies are used to deliver advertisements more relevant to you.
                        </p>
                      </div>
                      <div className="flex items-center">
                        <button
                          onClick={() =>
                            setPreferences({ ...preferences, marketing: !preferences.marketing })
                          }
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences.marketing
                              ? 'bg-[var(--color-main-1)]'
                              : 'bg-[var(--color-dark-4)]'
                          }`}
                          aria-pressed={preferences.marketing}
                          aria-label="Toggle marketing cookies"
                        >
                          <span
                            className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                              preferences.marketing ? 'translate-x-7' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <button
                      onClick={acceptNecessaryOnly}
                      className="btn-godlike btn-godlike-secondary btn-godlike-sm"
                    >
                      Necessary Only
                    </button>
                    <button
                      onClick={saveCurrentPreferences}
                      className="btn-godlike btn-godlike-primary btn-godlike-sm"
                    >
                      Save Preferences
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
